import { Box, Button, Paper, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import { useContext, useEffect, useState } from "react";
import { AUDIT_EVENT_TYPES, AuditEvent, AuditLogFilters, getAuditLogs, getEventTypeColor } from "../services/audit-log.service";
import { FilterChipRow, MultiFilterChip, PickerOption } from "../components/filter-chip.component";
import ClearableTextField from "../components/clearable-text-field.component";
import GlobalMessageContext from "../context/globalMessage.context";
import { dataTables } from "../styles";
import { format } from "date-fns";
import { ViewAuditEventDataModal } from "../components/modal.view-auditeventdata";
import { useStickyState } from "../util";

const AuditLogsView = () => {
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(100);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "createdAt", sort: "desc" },
  ]);

  const uxMessage = useContext(GlobalMessageContext);

  // Filter states
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  });
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [selectedEventTypes, setSelectedEventTypes] = useStickyState<string[]>(
    ["SALE", "REFUND", "STOCK_RECEIVE", "RECEIPT_REVERSAL"], // Default to only these 4 event types
    "auditLogs.selectedEventTypes"
  );
  const [sourceIdFilter, setSourceIdFilter] = useState<string>("");
  const [targetIdFilter, setTargetIdFilter] = useState<string>("");

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const filters: AuditLogFilters = {};
      
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (selectedEventTypes.length > 0) filters.eventTypes = selectedEventTypes;
      if (sourceIdFilter) filters.sourceId = sourceIdFilter;
      if (targetIdFilter) filters.targetId = targetIdFilter;

      const sortField = sortModel.length > 0 ? sortModel[0].field : "createdAt";
      const sortDirection = sortModel.length > 0 ? sortModel[0].sort || "desc" : "desc";

      const response = await getAuditLogs(page, pageSize, sortField, sortDirection, filters);
      // Add guid field for consistency with other tables
      const eventsWithGuid = response.data.data.map(event => ({
        ...event,
        guid: `audit-${event.id}`, // Add a synthetic guid for consistency
      }));
      setAuditEvents(eventsWithGuid);
      setTotalRows(response.data.totalCount);
    } catch (error) {
      console.error("Error loading audit logs:", error);
      uxMessage.setMessage({
        message: "Failed to load audit logs. Please try again later.",
        severity: "error",
      });
      setAuditEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortModel, startDate, endDate, selectedEventTypes, sourceIdFilter, targetIdFilter]);

  const handleClearFilters = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    setStartDate(yesterday);
    setEndDate(new Date());
    setSelectedEventTypes(["SALE", "REFUND", "STOCK_RECEIVE", "RECEIPT_REVERSAL"]); // Reset to default
    setSourceIdFilter("");
    setTargetIdFilter("");
    setPage(0);
    // Reload will be triggered by useEffect due to filter changes
  };

  const eventTypeOptions: PickerOption[] = AUDIT_EVENT_TYPES.map((t) => ({
    value: t.value,
    label: t.label,
    group: t.group,
    color: t.color,
  }));

  const columns: GridColDef[] = [
    { field: "id" },
    { field: "guid" },
    {
      field: "createdAt",
      headerName: "Date/Time",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => (
        <span title={params.value}>
          {format(new Date(params.value), "dd/MM/yyyy HH:mm:ss")}
        </span>
      ),
    },
    {
      field: "eventType",
      headerName: "Event Type",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <span
          style={{
            color: getEventTypeColor(params.value),
            fontWeight: "bold",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "sourceId",
      headerName: "Source ID",
      minWidth: 120,
      flex: 1,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "targetId",
      headerName: "Target ID",
      minWidth: 120,
      flex: 1,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "actions",
      headerName: "View",
      minWidth: 80,
      flex: 0.5,
      sortable: false,
      align: "left",
      renderCell: (params) => (
        <ViewAuditEventDataModal
          eventData={params.row.data}
          eventType={params.row.eventType}
          eventId={params.row.id}
          size="small"
        />
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: "100%", width: "100%" }}>
        <Typography variant="h1" gutterBottom>
          Audit Logs
        </Typography>

        {/* Filter Controls */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', marginBottom: 2, width: '100%' }}>
          <DatePicker
            label="Start Date"
            value={startDate} 
            onChange={(newValue) => {
              setPage(0);
              setStartDate(newValue);
            }}
            slotProps={{ textField: { sx: { minWidth: 200, flex: 1 } } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => {
              setPage(0);
              setEndDate(newValue);
            }}
            slotProps={{ textField: { sx: { minWidth: 200, flex: 1 } } }}
          />
          <ClearableTextField
            label="Source ID"
            value={sourceIdFilter}
            onChange={(v) => {
              setPage(0);
              setSourceIdFilter(v);
            }}
            placeholder="Filter by Source ID"
            sx={{ minWidth: 200, flex: 1 }}
          />
          <ClearableTextField
            label="Target ID"
            value={targetIdFilter}
            onChange={(v) => {
              setPage(0);
              setTargetIdFilter(v);
            }}
            placeholder="Filter by Target ID"
            sx={{ minWidth: 200, flex: 1 }}
          />
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            sx={{ height: 56 }}
            size="small"
          >
            Reset Filters
          </Button>
        </Box>
        
        {/* Event type filter, in the same chip row as the product list */}
        <FilterChipRow testId="audit-filter-chips">
          <MultiFilterChip
            name="Event type"
            anyLabel="All"
            value={selectedEventTypes}
            options={eventTypeOptions}
            onChange={(next) => {
              setPage(0);
              setSelectedEventTypes(next);
            }}
            searchable
            searchPlaceholder="Find an event type..."
            testId="filter-chip-event-type"
          />
        </FilterChipRow>

        {/* Data Grid */}
        <Paper
          sx={{
            mt: 2,
            p: "2px 4px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <DataGrid
            rows={auditEvents}
            columns={columns}
            loading={loading}
            paginationMode="server"
            sortingMode="server"
            density="compact"
            rowCount={totalRows}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(newModel) => {
              setPage(newModel.page);
              setPageSize(newModel.pageSize);
            }}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            sortModel={sortModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            columnVisibilityModel={{
              id: false,
              guid: false,
            }}
            sx={dataTables}
          />
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default AuditLogsView;